import {Injectable} from '@angular/core';
import {ComplianceItemApi} from '../api/compliance-item.api';
import {CplItem} from '../model/cpl-item.model';
import {UnitOfMeasure} from '../supervision-management/compliance-items/compliance-item/enums/unit-of-measure.enum';
import {UserAssignmentRequest} from '../model/user-assignment-request.model';
import {CplCycle} from '../model/cpl-cycle.model';
import { finalize } from 'rxjs/operators';
import { CplItemDuplicateData } from '../model/cpl-item-duplicate-data.model';
import { SessionService } from '@edl-development/edl-common';

@Injectable()
export class ComplianceItemService {

    constructor(
        private complianceItemApi: ComplianceItemApi,
        private sessionService:SessionService
    ) {}

    getComplianceItem = (cplId: string, cycleID: string) => {
        this.sessionService.AppLoading(true);

        return this.complianceItemApi.getComplianceItem(cplId, cycleID)
            .pipe(
                finalize(() => this.sessionService.AppLoading(false))
            );
    }

    getMyComplianceItems = (offset: number, limit: number, orderBy: string, ascending: boolean, searchBy?: string, filterStatusBy?: number, filterByTag?:string, updateStatusCounts:boolean=true) => {
        this.sessionService.AppLoading(true);

        return this.complianceItemApi.getMyComplianceItems(offset, limit, orderBy, ascending, searchBy, filterStatusBy, filterByTag)
            .pipe(
                finalize(() => this.sessionService.AppLoading(false))
            )
    }

    getComplianceItemsAssignedToUser(userId: string) {
        this.sessionService.AppLoading(true);

        return this.complianceItemApi.getComplianceItemsAssignedToUser(userId)
            .pipe(
                finalize(() => this.sessionService.AppLoading(false))
            );
    }

    getComplianceItems(offset: number, limit: number, orderBy: string, ascending: boolean, searchBy?: string, status?: number, tag?:number, updateStatusCounts:boolean=true) {
        this.sessionService.AppLoading(true);

        return this.complianceItemApi.getComplianceItems(offset, limit, orderBy, ascending, searchBy, status, tag)
            .pipe(
                finalize(() => this.sessionService.AppLoading(false))
            );
    }

    createComplianceItem = (complianceItem: CplItem) => this.complianceItemApi.createComplianceItem(complianceItem);

    updateComplianceItem = (complianceItem: CplItem) => this.complianceItemApi.updateComplianceItem(complianceItem);

    deleteComplianceItem = (complianceItem: CplItem) => this.complianceItemApi.deleteComplianceItem(complianceItem);

    restoreComplianceItem = (complianceItem: CplItem) => this.complianceItemApi.restoreComplianceItem(complianceItem);

    removeComplianceItem = (complianceItem: CplItem) => this.complianceItemApi.removeComplianceItem(complianceItem);

    archiveComplianceItem = (complianceItem: CplItem) => this.complianceItemApi.archiveComplianceItem(complianceItem);

    getArchiveWarnings = (cplId: string) => this.complianceItemApi.getArchiveWarnings(cplId);

    activateComplianceItem = (complianceItem: CplItem) => this.complianceItemApi.activateComplianceItem(complianceItem);

    convertComplianceItemToTemplate = (complianceItemDuplicateData: CplItemDuplicateData) => 
        this.complianceItemApi.convertComplianceItemToTemplate(complianceItemDuplicateData);

    duplicateComplianceItem = (complianceItemDuplicateData: CplItemDuplicateData) => 
        this.complianceItemApi.duplicateComplianceItem(complianceItemDuplicateData);

    getItemUserAssignments = ({
        cplId, 
        cycleId, 
        offset, 
        limit, 
        orderBy, 
        ascending, 
        searchBy, 
        statusId, 
        a48statusId,
        approverUserId,
        groupIds
    } : {
        cplId: string, 
        cycleId: string, 
        offset: number, 
        limit: number, 
        orderBy?: string, 
        ascending?: boolean,
        searchBy?:string, 
        statusId?:number, 
        a48statusId?:number,
        approverUserId?:string,
        groupIds?:string[]
    }) => this.complianceItemApi.getItemUserAssignments({cplId, cycleId, offset, limit, orderBy, ascending, searchBy, statusId: statusId ? statusId : -1, a48statusId, approverUserId, groupIds });

    getUserUserAssignments = ({
        cplId, 
        offset, 
        limit, 
        orderBy, 
        ascending,
        searchBy, 
        statusId, 
        a48statusId,
        approverUserId,
        groupIds
    } : { 
        cplId: string,
        offset: number, 
        limit: number, 
        orderBy?: string, 
        ascending?: boolean,
        searchBy?:string, 
        statusId?:number, 
        a48statusId?:number,
        approverUserId?:string,
        groupIds?:string[]
    }) => this.complianceItemApi.getUserUserAssignments({ cplId, offset, limit, orderBy, ascending, searchBy, statusId: statusId ? statusId : -1, a48statusId, approverUserId, groupIds });

    removeUserAssignments = (request: UserAssignmentRequest) => this.complianceItemApi.removeUserAssignments(request);

    createUserAssignments = (request: UserAssignmentRequest) => this.complianceItemApi.createUserAssignments(request);

    updateUserAssignments = (userCycles: CplCycle[]) => this.complianceItemApi.updateUserAssignments(userCycles);

    getUserSummary = (userId: string, offset: number, limit: number, orderBy: string, ascending: boolean, searchBy?: string, updateStatusCounts:boolean=true) =>
        this.complianceItemApi.getUserSummary(userId, offset, limit, orderBy, ascending, searchBy);

    getExpiredCycles = (cplId: string, offset: number, limit: number, orderBy: string, ascending: boolean, searchBy?: string, baseUrl?: string) =>
        this.complianceItemApi.getExpiredCycles(cplId, offset, limit, orderBy, ascending, searchBy, baseUrl);

    getUserCycleHistory = (cplId: string, userId: string, offset: number, limit: number, orderBy: string, ascending: boolean, searchBy?: string, baseUrl?: string) =>
        this.complianceItemApi.getUserCycleHistory(cplId, userId, offset, limit, orderBy, ascending, searchBy, baseUrl);

    getUserCycleHistoryByCycle = (cycleId: string,  offset: number, limit: number, orderBy: string, ascending: boolean, searchBy?: string, baseUrl?: string) =>
        this.complianceItemApi.getUserCycleHistoryByCycle(cycleId, offset, limit, orderBy, ascending, searchBy, baseUrl);

    getUserCompliance = (offset: number, limit: number, orderBy: string, ascending: boolean, searchBy?: string, baseUrl?: string) =>
        this.complianceItemApi.getUserCompliance(offset, limit, orderBy, ascending, searchBy, baseUrl);

    getTags = (searchBy: string) => this.complianceItemApi.getTags(searchBy);

    createBulkUserAssignments = (fileList: FileList, ItemID: string) => this.complianceItemApi.createBulkUserAssignments(fileList, ItemID);

    createBulkSingleUserToMultipleAssignments = (requests: UserAssignmentRequest[]) => this.complianceItemApi.createBulkSingleUserToMultipleAssignments(requests);

    unitOfMeasure(uom: number): string {
        let retval;

        if (typeof uom === 'string') {
            uom = parseInt(uom);
        }

        switch (uom) {
            case UnitOfMeasure.DAY:
                retval = ' Day';
                break;

            case UnitOfMeasure.WEEK:
                retval = ' Week';
                break;

            case UnitOfMeasure.MONTH:
                retval = ' Month';
                break;

            case UnitOfMeasure.YEAR:
                retval = ' Year';
                break;
        }

        return retval;
    } 

    getPrintableRoster = (complianceItemService) => this.complianceItemApi.getPrintableRoster(complianceItemService);

    getUserArchiveSummary = (userId: string, updateStatusCounts:boolean=true) => this.complianceItemApi.getUserArchiveSummary(userId);
}